import React, { useEffect } from "react";
import { Modal, Form, Input, Button, Upload, Row, Col } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Place } from "../../types";
import { ActivationStatus } from "../../types";

interface PlaceModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (values: any) => void;
    editingRecord: Place | null;
    establishmentLocationId: string;
    loading: boolean;
}

const PlaceModal: React.FC<PlaceModalProps> = ({
    visible,
    onCancel,
    onSave,
    editingRecord,
    establishmentLocationId,
    loading,
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            if (editingRecord) {
                form.setFieldsValue({
                    ...editingRecord,
                    icon: editingRecord.fullUrl ? [{ url: editingRecord.fullUrl, name: editingRecord.icon }] : [],
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, editingRecord, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    return (
        <Modal
            title={editingRecord ? "Edit Place" : "Add Place"}
            open={visible}
            onCancel={onCancel}
            width={800}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" loading={loading} onClick={handleSave}>
                    {editingRecord ? "Update" : "Create"}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" initialValues={{ status: ActivationStatus.Active }}>
                <Form.Item name="id" hidden>
                    <Input />
                </Form.Item>

                <Form.Item name="establishmentLocationId" hidden initialValue={establishmentLocationId}>
                    <Input />
                </Form.Item>

                <Form.Item name="status" hidden initialValue={ActivationStatus.Active}>
                    <Input />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Place Name"
                            name="name"
                            rules={[{ required: true, message: "Please enter place name" }]}
                        >
                            <Input placeholder="e.g., Terminal 1, Gate A, Baggage Claim" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Icon" name="icon" valuePropName="fileList" getValueFromEvent={normFile}>
                            <Upload
                                beforeUpload={() => false}
                                maxCount={1}
                                accept=".svg,.png,.jpg,.jpeg,.webp"
                                listType="picture"
                            >
                                <Button icon={<UploadOutlined />}>Upload Icon</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label="Description" name="description">
                            <Input.TextArea
                                rows={2}
                                placeholder="e.g., International departures terminal"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label="Street Address" name="address">
                            <Input placeholder="e.g., 1 World Way, Terminal 1" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="City" name="city">
                            <Input placeholder="e.g., Los Angeles" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="State/Province" name="state">
                            <Input placeholder="e.g., California" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Country" name="country">
                            <Input placeholder="e.g., United States" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="ZIP/Postal Code" name="zipCode">
                            <Input placeholder="e.g., 90045" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Phone" name="phone">
                            <Input placeholder="e.g., +1-310-646-5252" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Email" name="email">
                            <Input placeholder="e.g., terminal1@lax.com" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default PlaceModal;
